/**
 * WorkflowTestModal Component
 *
 * Modal for selecting test data before executing workflow test.
 * Analyzes workflow trigger nodes and shows appropriate dropdowns
 * based on entityType configuration.
 *
 * Follows the always-light theme pattern for modals.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/shared/components/ui/Button';
import { Select } from '@/shared/components/ui/Select';
import { useEscapeKey } from '@/shared/components/ui/Modal';
import { useConversations } from '@/modules/chat/hooks/useConversations';
import { useLeads } from '@/modules/crm/leads/hooks/useLeads';
import { useContacts } from '@/modules/chat/hooks/useContacts';
import { useTickets } from '@/modules/helpdesk/tickets/hooks/useTickets';
import { useCalendarEvents } from '@/modules/calendar/hooks/useCalendarEvents';
import type { Node } from 'reactflow';
import { entityForEventType } from '../utils/event-entities';
import { Icon } from '@/shared/icons'
interface WorkflowTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** `triggerNodeId`: o gatilho a simular — só vem quando o workflow tem vários. */
  onConfirm: (testContext: TestContext, triggerNodeId?: string) => void;
  nodes: Node[];
  isStarting?: boolean;
}

export interface TestContext {
  conversation?: {
    id: string;
    name: string;
  };
  lead?: {
    id: string;
    name: string;
  };
  contact?: {
    id: string;
    name: string;
  };
  ticket?: {
    id: string;
    name: string;
  };
  event?: {
    id: string;
    name: string;
  };
}

interface TriggerRequirement {
  nodeId: string;
  nodeLabel: string;
  triggerType: string;
  entityType: 'conversation' | 'contact' | 'lead' | 'ticket' | 'event';
}

/**
 * WorkflowTestModal Component
 *
 * Displays a modal for selecting test data before running workflow test.
 */
export const WorkflowTestModal: React.FC<WorkflowTestModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  nodes,
  isStarting = false,
}) => {
  const [selectedConversation, setSelectedConversation] = useState<string>('');
  const [selectedLead, setSelectedLead] = useState<string>('');
  const [selectedContact, setSelectedContact] = useState<string>('');
  const [selectedTicket, setSelectedTicket] = useState<string>('');
  const [selectedEvent, setSelectedEvent] = useState<string>('');

  // Vários gatilhos (23/09/2026): o teste roda o fluxo a partir de UM deles —
  // escolhido aqui — e pede só os dados desse.
  const triggerNodes = useMemo(
    () => (nodes ?? []).filter((node) => String(node.data?.type ?? '').startsWith('trigger_')),
    [nodes],
  );
  const [selectedTriggerId, setSelectedTriggerId] = useState<string>('');
  const activeTriggerId = triggerNodes.some((n) => n.id === selectedTriggerId)
    ? selectedTriggerId
    : (triggerNodes[0]?.id ?? '');

  // Analyze trigger nodes FIRST — determina quais entity types são necessários
  // para condicionar os hooks de fetch (regra 14: lazy fetching em modais)
  const triggerRequirements = useMemo<TriggerRequirement[]>(() => {
    if (!nodes) return [];

    const requirements: TriggerRequirement[] = [];
    const seenEntityTypes = new Set<string>();

    nodes.forEach((node) => {
      // Check if it's a trigger node
      const nodeType = node.data?.type as string;
      if (!nodeType?.startsWith('trigger_')) return;
      // Com vários gatilhos, só o escolhido pede dados.
      if (triggerNodes.length > 1 && node.id !== activeTriggerId) return;

      // Get entityType from config (inactivity/date_field) OR derive from eventType (trigger_event)
      const explicitEntityType = node.data?.config?.entityType as string | undefined;
      const eventType = (node.data?.config?.eventType as string | undefined) ?? '';
      // Fonte única (event-entities.ts) — a cascata copiada aqui parava em
      // `contact.` e o botão "Testar" mandava evento de agenda sem entidade.
      const derivedFromEvent = entityForEventType(eventType) ?? undefined;
      const entityType = explicitEntityType ?? derivedFromEvent;
      if (!entityType) return;

      // Only add one requirement per entity type
      if (seenEntityTypes.has(entityType)) return;
      seenEntityTypes.add(entityType);

      requirements.push({
        nodeId: node.id,
        nodeLabel: node.data?.label || nodeType,
        triggerType: nodeType,
        entityType: entityType as 'conversation' | 'contact' | 'lead' | 'ticket' | 'event',
      });
    });

    return requirements;
  }, [nodes, triggerNodes.length, activeTriggerId]);

  // Quais entity types são necessários?
  const needsConversation = triggerRequirements.some((r) => r.entityType === 'conversation');
  const needsLead = triggerRequirements.some((r) => r.entityType === 'lead');
  const needsContact = triggerRequirements.some((r) => r.entityType === 'contact');
  const needsTicket = triggerRequirements.some((r) => r.entityType === 'ticket');
  const needsEvent = triggerRequirements.some((r) => r.entityType === 'event');

  // Estabilizar referências dos params para evitar re-fetch desnecessário no React Query.
  // Objetos inline a cada render criam queryKeys novas → fetch duplicado.
  const conversationQuery = useMemo(() => ({
    limit: 100,
    filters: { status: ['waiting', 'active'] as Array<'waiting' | 'active' | 'closed'> },
  }), []);
  const defaultQuery = useMemo(() => ({ limit: 100 }), []);
  const startTimeFrom = useMemo(() => new Date().toISOString(), []);
  const eventQuery = useMemo(() => ({
    limit: 100, sortBy: 'startTime' as const, sortOrder: 'asc' as const,
    filters: { startTimeFrom, hasContactId: true },
  }), [startTimeFrom]);

  // Fetch data para dropdowns — condicionado ao isOpen + entity type necessário
  // (regra 14: lazy fetching em modais; só busca o que o workflow realmente exige).
  const { data: conversationsData, isLoading: isLoadingConversations } = useConversations(conversationQuery, { enabled: isOpen && needsConversation });
  const { data: leadsData, isLoading: isLoadingLeads } = useLeads(defaultQuery, { enabled: isOpen && needsLead });
  const { data: contactsData, isLoading: isLoadingContacts } = useContacts(defaultQuery, isOpen && needsContact);
  const { data: ticketsData, isLoading: isLoadingTickets } = useTickets(defaultQuery, { enabled: isOpen && needsTicket });
  const { data: eventsData, isLoading: isLoadingEvents } = useCalendarEvents(eventQuery, { enabled: isOpen && needsEvent });

  // isLoading APENAS dos hooks cujo entityType é necessário para este workflow.
  // Antes: qualquer hook lento (ex: calendar events) travava o dropdown inteiro.
  const isLoading =
    (needsConversation && isLoadingConversations) ||
    (needsLead && isLoadingLeads) ||
    (needsContact && isLoadingContacts) ||
    (needsTicket && isLoadingTickets) ||
    (needsEvent && isLoadingEvents);

  // Reset selections when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedTriggerId('');
      setSelectedConversation('');
      setSelectedLead('');
      setSelectedContact('');
      setSelectedTicket('');
      setSelectedEvent('');
    }
  }, [isOpen]);

  // Fecha o modal ao pressionar ESC
  useEscapeKey(onClose, isOpen);

  const handleConfirm = () => {
    const testContext: TestContext = {};

    // Build test context based on selections
    if (selectedConversation && conversationsData?.items) {
      const conv = conversationsData.items.find((c) => c.id === selectedConversation);
      if (conv) {
        testContext.conversation = {
          id: conv.id,
          name: conv.contact?.name || conv.contact?.phone || 'Conversa',
        };
      }
    }

    if (selectedLead && leadsData?.items) {
      const lead = leadsData.items.find((l) => l.id === selectedLead);
      if (lead) {
        testContext.lead = {
          id: lead.id,
          name: lead.description || 'Lead',
        };
      }
    }

    if (selectedContact && contactsData?.items) {
      const contact = contactsData.items.find((c) => c.id === selectedContact);
      if (contact) {
        testContext.contact = {
          id: contact.id,
          name: contact.name || contact.identifiers?.phone?.[0] || 'Contato',
        };
      }
    }

    if (selectedTicket && ticketsData?.items) {
      const ticket = ticketsData.items.find((t) => t.id === selectedTicket);
      if (ticket) {
        testContext.ticket = {
          id: ticket.id,
          name: ticket.title || `Ticket ${ticket.id.slice(-6)}`,
        };
      }
    }

    if (selectedEvent && eventsData?.items) {
      const event = eventsData.items.find((e) => e.id === selectedEvent);
      if (event) {
        testContext.event = {
          id: event.id,
          name: event.summary || 'Evento',
        };
      }
    }

    onConfirm(testContext, triggerNodes.length > 1 ? activeTriggerId : undefined);
  };

  // Check if all required selections are made
  const isValid = useMemo(() => {
    for (const req of triggerRequirements) {
      if (req.entityType === 'conversation' && !selectedConversation) return false;
      if (req.entityType === 'lead' && !selectedLead) return false;
      if (req.entityType === 'contact' && !selectedContact) return false;
      if (req.entityType === 'ticket' && !selectedTicket) return false;
      if (req.entityType === 'event' && !selectedEvent) return false;
    }
    return true;
  }, [triggerRequirements, selectedConversation, selectedLead, selectedContact, selectedTicket, selectedEvent]);

  // Check if no trigger nodes require entity selection
  const noRequirements = triggerRequirements.length === 0;

  // Helper to render entity icon
  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case 'conversation':
        return <Icon name="message-square" className="w-4 h-4" />;
      case 'lead':
        return <Icon name="users" className="w-4 h-4" />;
      case 'contact':
        return <Icon name="user" className="w-4 h-4" />;
      case 'ticket':
        return <Icon name="file-text" className="w-4 h-4" />;
      case 'event':
        return <Icon name="calendar" className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // Helper to render entity label
  const getEntityLabel = (entityType: string) => {
    switch (entityType) {
      case 'conversation':
        return 'Conversa';
      case 'lead':
        return 'Lead';
      case 'contact':
        return 'Contato';
      case 'ticket':
        return 'Ticket';
      case 'event':
        return 'Evento';
      default:
        return entityType;
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.34)' }}
    >
      <div
        data-testid="workflow-test-modal"
        className="relative flex flex-col w-full max-w-lg max-h-[88vh] overflow-hidden"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '14px',
          boxShadow: '0 18px 60px rgba(0, 0, 0, 0.22)',
          // Override CSS variables to light theme (modals are always light)
          '--theme-text': '#1e293b',
          '--theme-muted-text': '#6b7280',
          '--theme-input-background': '#ffffff',
          '--theme-input-text': '#1e293b',
          '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
          '--theme-input-focus': '#8b5cf6',
          '--theme-error-color': '#ef4444',
          '--theme-card-background': '#ffffff',
          '--theme-card-text': '#334155',
          '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
          '--theme-card-muted': '#94a3b8',
          '--theme-button-primary': '#8b5cf6',
          '--theme-button-primary-text': '#ffffff',
          '--theme-button-primary-hover': '#7c3aed',
          '--theme-primary': '#8b5cf6',
          '--theme-border': 'rgba(0, 0, 0, 0.06)',
          '--theme-component-background': '#ffffff',
        } as React.CSSProperties}
      >
        {/* Header */}
        <div
          className="flex-none flex items-center gap-2.5 px-4 py-3.5 border-b"
          style={{ borderColor: '#eef1f5' }}
        >
          <h2
            className="flex-1 min-w-0 font-bold"
            style={{ fontSize: 15, color: '#1e293b' }}
          >
            Testar Workflow
          </h2>
          <button
            onClick={onClose}
            className="flex-none flex items-center justify-center w-[30px] h-[30px] rounded-lg transition-colors"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Icon name="x" className="w-[18px] h-[18px]" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3.5 space-y-3.5">
          {/* Description */}
          <p
            style={{ fontSize: 12.5, color: '#6b7280' }}
          >
            {noRequirements
              ? 'Este workflow não possui triggers que necessitam de dados de teste. Clique em "Iniciar Teste" para executar o workflow.'
              : 'Selecione os dados que serão usados para simular os triggers do workflow durante o teste.'}
          </p>

          {/* Gatilho a simular — só com vários gatilhos (23/09/2026) */}
          {triggerNodes.length > 1 && (
            <div>
              <label
                className="flex items-center gap-2 mb-1.5"
                style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}
              >
                <Icon name="zap" className="w-4 h-4" />
                <span>Gatilho a simular</span>
              </label>
              <Select
                value={activeTriggerId}
                onChange={(value) => setSelectedTriggerId(value)}
                options={triggerNodes.map((node) => ({
                  value: node.id,
                  label: String(node.data?.label || node.data?.type || node.id),
                }))}
                data-testid="workflow-test-trigger"
              />
            </div>
          )}

          {/* Loading State */}
          {isLoading && !noRequirements && (
            <div className="flex items-center justify-center py-8">
              <div
                className="animate-spin rounded-full h-8 w-8 border-2"
                style={{ borderColor: '#8b5cf6', borderTopColor: 'transparent' }}
              />
            </div>
          )}

          {/* Entity Selection Fields */}
          {!isLoading && triggerRequirements.map((req) => (
            <div key={req.nodeId}>
              <label
                className="flex items-center gap-2 mb-1.5"
                style={{ fontSize: 11.5, fontWeight: 700, color: '#475569' }}
              >
                {getEntityIcon(req.entityType)}
                <span>{getEntityLabel(req.entityType)}</span>
                <span
                  className="px-2 py-0.5 rounded-full font-medium"
                  style={{ fontSize: 11, backgroundColor: 'rgba(0, 0, 0, 0.04)', color: '#6b7280' }}
                >
                  {req.nodeLabel}
                </span>
              </label>

              {/* Conversation Dropdown — apenas individuais (grupos não são permitidos em automações) */}
              {req.entityType === 'conversation' && (
                <Select
                  value={selectedConversation}
                  onChange={(value) => setSelectedConversation(value)}
                  placeholder="Selecione uma conversa..."
                  options={conversationsData?.items
                    ?.filter((conv) => !conv.groupId)
                    .map((conv) => ({
                      value: conv.id,
                      label: conv.contact?.name || conv.contact?.phone || conv.id,
                    })) ?? []}
                />
              )}

              {/* Lead Dropdown */}
              {req.entityType === 'lead' && (
                <Select
                  value={selectedLead}
                  onChange={(value) => setSelectedLead(value)}
                  placeholder="Selecione um lead..."
                  options={leadsData?.items?.map((lead) => ({
                    value: lead.id,
                    label: lead.description || `Lead ${lead.id.slice(-6)}`,
                  })) ?? []}
                />
              )}

              {/* Contact Dropdown */}
              {req.entityType === 'contact' && (
                <Select
                  value={selectedContact}
                  onChange={(value) => setSelectedContact(value)}
                  placeholder="Selecione um contato..."
                  options={contactsData?.items?.map((contact) => ({
                    value: contact.id,
                    label: contact.name || contact.identifiers?.phone?.[0] || contact.id,
                  })) ?? []}
                />
              )}

              {/* Ticket Dropdown */}
              {req.entityType === 'ticket' && (
                <Select
                  value={selectedTicket}
                  onChange={(value) => setSelectedTicket(value)}
                  placeholder="Selecione um ticket..."
                  options={ticketsData?.items?.map((ticket) => ({
                    value: ticket.id,
                    label: ticket.title || `Ticket ${ticket.id.slice(-6)}`,
                  })) ?? []}
                />
              )}

              {/* Event Dropdown */}
              {req.entityType === 'event' && (
                <Select
                  value={selectedEvent}
                  onChange={(value) => setSelectedEvent(value)}
                  placeholder="Selecione um evento..."
                  options={eventsData?.items?.map((event) => {
                    const eventDate = event.startTime
                      ? new Date(event.startTime).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        }) + ' às ' + new Date(event.startTime).toLocaleTimeString('pt-BR', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '';
                    const displayName = event.summary || 'Evento sem título';
                    return {
                      value: event.id,
                      label: eventDate ? `${eventDate} - ${displayName}` : displayName,
                    };
                  }) ?? []}
                />
              )}
            </div>
          ))}

          {/* Info Box */}
          {!noRequirements && (
            <div
              className="flex items-start gap-2.5 p-3 rounded-[10px]"
              style={{ backgroundColor: 'rgba(59, 130, 246, 0.08)' }}
            >
              <Icon name="alert-circle" className="w-[18px] h-[18px] flex-shrink-0 mt-px" style={{ color: '#3b82f6' }} />
              <p style={{ fontSize: 12, color: '#2563eb', lineHeight: 1.5 }}>
                O teste executara o workflow usando os dados selecionados para simular os eventos de trigger.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex-none flex items-center gap-2.5 px-4 py-3 border-t"
          style={{
            backgroundColor: '#ffffff',
            borderColor: '#eef1f5',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-[34px] rounded-lg border text-sm font-medium transition-colors"
            style={{
              color: '#475569',
              backgroundColor: '#ffffff',
              borderColor: '#d7dde5',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
            }}
            disabled={isStarting}
          >
            Cancelar
          </button>
          <Button
            onClick={handleConfirm}
            disabled={isStarting || (!noRequirements && !isValid) || isLoading}
            data-testid="workflow-test-run"
            className="flex-1"
          >
            {isStarting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Iniciando...
              </>
            ) : (
              <>
                <Icon name="play" className="w-4 h-4 mr-2" />
                Iniciar Teste
              </>
            )}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WorkflowTestModal;
